import { useEffect, useState } from "react";
import { CalendarDays, ExternalLink, Megaphone, TriangleAlert } from "lucide-react";

import { EmptyPanel, PageHero } from "@/components/interior-page";
import Loader from "@/components/ui/loader";
import { fetchPressFeed, formatPressDate, type PressFeed, type PressItem } from "@/lib/press";

/**
 * Press & media coverage.
 *
 * Unlike the rest of the Resource library there is no admin screen behind this
 * page: the API reads the list straight from the public press page on
 * businesslocating.com (see app/services/press_feed.py), so anything marketing
 * publishes there shows up here without a second round of data entry.
 *
 * Every item links out to the publication that ran the story — there is no
 * internal detail route, because the portal never holds the article itself.
 */

/**
 * The outlet's logo, on a light chip.
 *
 * Deliberately NOT theme-aware: these are third-party brand marks drawn in
 * their own fixed colours (usually dark ink on transparent), so painting the
 * chip with the app background would make half of them vanish under the dark
 * theme. A white chip is what the marketing site shows and what the brands
 * expect.
 *
 * Renders nothing at all when there is no logo or the image fails to load —
 * the outlet's name sits beside it either way, so nothing is lost.
 */
function OutletLogo({ src, outlet }: { src: string | null; outlet: string | null }) {
  const [failed, setFailed] = useState(false);
  if (!src || failed) return null;
  return (
    <span className="inline-flex h-9 shrink-0 items-center justify-center rounded-lg border border-border/70 bg-white px-2">
      <img
        src={src}
        alt={outlet ? `${outlet} logo` : ""}
        loading="lazy"
        className="block h-6 w-auto max-w-[104px] object-contain"
        onError={() => setFailed(true)}
      />
    </span>
  );
}

function PressCard({ item }: { item: PressItem }) {
  const date = formatPressDate(item);
  return (
    <a
      href={item.url}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex flex-col rounded-2xl border border-border bg-card/60 p-5 transition hover:border-primary/40 hover:shadow-sm"
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <OutletLogo src={item.outlet_logo_url} outlet={item.outlet} />
        {item.outlet && (
          <span className="text-sm font-semibold text-foreground">{item.outlet}</span>
        )}
        {date && (
          <span className="inline-flex items-center gap-1.5 text-[0.7rem] font-medium uppercase tracking-wide text-muted-foreground">
            <CalendarDays className="h-3.5 w-3.5" />
            {date}
          </span>
        )}
        {item.featured && (
          <span className="rounded-full border border-primary/40 bg-primary/10 px-2.5 py-0.5 text-[0.65rem] font-bold uppercase tracking-wide text-primary">
            Featured
          </span>
        )}
      </div>

      <h3 className="heading-bold mt-3 text-base leading-snug text-foreground">{item.title}</h3>
      {item.summary && (
        <p className="mt-2 text-sm leading-6 text-muted-foreground">{item.summary}</p>
      )}

      {/* Spacer pins the footer to the card bottom so all footers align. */}
      <div className="flex-1" />
      <div className="mt-4 flex items-center justify-between gap-2 border-t border-border/50 pt-3.5">
        {/* The gold sits on this span, not on the <a>: index.css has an
            unlayered `a { color: inherit }` that outranks Tailwind's text-*
            utilities, so text-primary on the anchor itself would do nothing. */}
        <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary">
          Read the article
          <ExternalLink className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </span>
      </div>
    </a>
  );
}

export default function PressPage() {
  const [feed, setFeed] = useState<PressFeed | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    fetchPressFeed()
      .then(setFeed)
      .catch(() => setFailed(true));
  }, []);

  // The API answers 200 with an empty list when the marketing site is
  // unreachable and it has nothing cached, so an empty feed is a failure to
  // load rather than "no coverage yet" — there is always coverage.
  const unavailable = failed || (feed !== null && feed.items.length === 0);

  return (
    <div className="public-stage pb-8">
      <div className="w-full px-4 py-8 sm:px-6 lg:px-8">
        {/* Shared Dark · Gold interior hero — same header as the rest of the
            Resource library. */}
        <div className="mb-8">
          <PageHero
            eyebrow="Resource Library"
            icon={Megaphone}
            title="Press &"
            titleAccent="Media Coverage"
            description={
              "Where BusinessLocating.com has been featured — published straight from our press room, newest first."
            }
            actions={
              feed?.source_url ? (
                <a
                  href={feed.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-primary/60 bg-gold-gradient px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-[filter] hover:brightness-[1.06]"
                >
                  Visit the press room
                  <ExternalLink className="h-4 w-4" />
                </a>
              ) : undefined
            }
          />
        </div>

        {/* Say so rather than passing a cached copy off as current. */}
        {feed?.stale && feed.items.length > 0 && (
          <div className="mb-5 flex items-start gap-2.5 rounded-xl border border-border bg-muted/30 px-4 py-3 text-xs leading-5 text-muted-foreground">
            <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
            <span>
              Our press room couldn&apos;t be reached just now, so this is the last copy we
              retrieved. It may be missing the most recent coverage.
            </span>
          </div>
        )}

        {unavailable ? (
          <EmptyPanel
            icon={TriangleAlert}
            title="Couldn't load press coverage"
            description="Something went wrong fetching our latest press coverage. Please refresh the page to try again."
          />
        ) : feed === null ? (
          <div className="flex justify-center py-12"><Loader /></div>
        ) : (
          // Two columns rather than the News grid's three: press cards carry a
          // logo row plus a full headline, which is unreadable at a third of
          // the page width.
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {feed.items.map((item) => (
              <PressCard key={item.url} item={item} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
